/* eslint-disable max-lines */
import React, {
    useState, useEffect, useContext
} from 'react';
import {
    Button
} from 'shared/components/ui/button';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose,
} from 'shared/components/ui/dialog';
import {
    Bot, Clock, Settings2, User, Pencil, InfoIcon
} from 'lucide-react';
import {
    ScrollArea, ScrollBar
} from 'shared/components/ui/scroll-area';
import {
    Context
} from 'shared/context/GlobalState';
import {
    Card, CardContent, CardDescription, CardHeader, CardTitle
} from 'shared/components/ui/card';
import {
    Label
} from 'shared/components/ui/label';
import {
    Badge
} from 'shared/components/ui/badge';
import {
    FilterBar, SearchBar
} from 'builders/builderInternal/components/Instructions/Filters';
import handler from 'builders/builderOpcode/helper/OpCodeBuilderHandler';
import {
    useParams
} from 'react-router-dom';
import {
    OrganizationContext
} from 'shared/context/OrganizationContext';
import {
    modelService
} from 'shared/api/services/BEX/models.services';
import {
    instructionService
} from 'shared/api/services/BEX/instruction.service';
import {
    Tooltip,
    TooltipProvider,
    TooltipTrigger
} from 'shared/components/ui/tooltip';
import KBTooltipContent from './KBTooltipContent';

export default function GeneralInstructionSelectionModal({
    instruction, setInstruction
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedInstruction, setSelectedInstruction] = useState(null);
    const [instructionDetails, setInstructionDetails] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [instructions, setInstructions] = useState([]);
    // const [modelProviders, setModelProviders] = useState([]);
    const {
        selectedOrganization
    } = useContext(OrganizationContext);

    const [IsInstructionLoading, setIsInstructionLoading] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [instructionUpdate, setInstructionUpdate] = useState(false);
    const {
        addNewNotifcation
    } = useContext(Context);
    const [selectType, setSelectType] = useState('All');
    const {
        oragID, subOragID
    } = useParams();

    useEffect(() => {
        if (isOpen) {
            setSearchTerm('');
        }
    }, [isOpen]);

    const handleReloadModels = async (hardRefresh = false) => {
        const body = {
            provider: selectedOrganization.providers || [],
            filter: {
                Watson: {
                    region: 'All',
                    showInactive: false
                },
                Groq: {
                    showInactive: false
                }
            }
        };
        const res = await modelService.getModels(body, hardRefresh, oragID);
        const models = res.data || [];
        models.sort((a, b) => {
            let providerComparison = a.provider_type.localeCompare(b.provider_type);
            return providerComparison !== 0 ? providerComparison : a.model_id.localeCompare(b.model_id);
        });
        // setModelProviders(models);

        return models;
    };

    const fetchInstructions = async () => {
        try {
            setInstructions([]); // Reset instructions
            setIsInstructionLoading(true);

            const models = await handleReloadModels();
            const modelIdSet = new Set(models.map(model => model.model_id));

            const res = await instructionService.listModels(
                oragID,
                subOragID,
            );

            const instructios = res.data || [];
            const fetchedInstructions = instructios.map((instruction) => {
                const modifiedByMember = selectedOrganization.members.find(member => member.id._id === instruction.modified_by);
                const userIdMember = selectedOrganization.members.find(member => member.id._id === instruction.createdBy);

                const parameters = instruction.parameters || {};
                // update the parameters for IBM model from sampling to sample
                if (instruction.model_type === 'IBM' && instruction.parameters?.decoding_method === 'sampling') {
                    parameters['decoding_method'] = 'sample';
                }

                return {
                    ...instruction,
                    parameters,
                    modified_by: modifiedByMember ? modifiedByMember.id.name : instruction.modified_by,
                    user_id: userIdMember ? userIdMember.id.name : instruction.createdBy,
                    isModelProviderExist: modelIdSet.has(instruction.modelId)
                };
            });

            setInstructions(fetchedInstructions);
        } catch (error) {
            console.error('Error fetching instructions:', error);
        } finally {
            setIsInstructionLoading(false);
        }
    };

    useEffect(() => {
        fetchInstructions();
    }, []);

    const handleInstructionSelect = (instruction) => {
        if (selectedInstruction === instruction.unique_name) {
            setSelectedInstruction(null);
            setInstructionDetails(null);
        } else {
            setSelectedInstruction(instruction.unique_name);
            setInstructionDetails(instruction);
        }
    };

    const handleSelectInstruction = () => {
        if (!selectedInstruction) {
            addNewNotifcation('Please select an instruction first.', 'warning');
            return;
        }

        // check if the selected instruction is valid
        const selectedInstructionDetails = instructions.find(instruction => instruction.unique_name === selectedInstruction);
        if (selectedInstructionDetails.isModelProviderExist === false) {
            addNewNotifcation('This instruction is Invalid', 'warning');
            return;
        }

        setSelectedInstruction(selectedInstructionDetails);
        setInstruction(selectedInstructionDetails);
        setIsOpen(false);
    };

    const handleFilterModels = () => {
        return instructions.filter(instruction =>
            (instruction.unique_name?.toLowerCase().includes(searchTerm.toLowerCase())) &&
            (selectType === 'All' || instruction.model_type === selectType) // search within selected type if not All
        );
    };

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild className="w-full">
                <Button variant="outline" className="text-left overflow-hidden">
                    {instruction?.unique_name || 'Select an Instruction'}
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[90vw] sm:max-h-[90vh]">
                <DialogHeader>
                    <DialogTitle>Select an Instruction</DialogTitle>
                    <DialogDescription>
                        Choose an instruction and view its details.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="flex items-center">
                        <div className="flex flex-col w-full sm:flex-row gap-4 items-center p-4">
                            <div className="flex-grow">
                                <SearchBar onSearch={(value) => setSearchTerm(value)} />
                            </div>
                            <FilterBar
                                onSearch={(value) => setSelectType(value)}
                                selectModelType={selectType}
                            />
                        </div>
                        <Button onClick={fetchInstructions} className="flex gap-2" type="button" disabled={IsInstructionLoading}>
                            {IsInstructionLoading ? 'Loading Instructions...' : 'Reload Instructions'}
                        </Button>
                    </div>

                    <div className="flex flex-col md:flex-row gap-4">
                        {handleFilterModels().length === 0 && (
                            <div className="flex items-center justify-center w-full text-gray-500">
                                <span>No instruction(s) saved.</span>
                            </div>
                        )}
                        <ScrollArea className="h-[400px] flex-grow">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-3 gap-4 p-4">
                                {handleFilterModels().length > 0 && (
                                    handleFilterModels().map((instruction) => (
                                        <Button
                                            key={instruction?._id || instruction.unique_name}
                                            variant={selectedInstruction === instruction.unique_name ? 'default' : 'outline'}
                                            onClick={() => handleInstructionSelect(instruction)}
                                            className={`justify-start h-auto text-left ${instruction?.isModelProviderExist === false ? 'bg-amber-100 hover:bg-amber-50 text-amber-700 border-amber-300' : ''}`}
                                        >
                                            <div className="w-full py-2">
                                                <div className='flex w-full justify-between gap-2 items-center'>
                                                    <div className="font-semibold w-full truncate flex gap-1 items-center">
                                                        {instruction?.isModelProviderExist === false && (
                                                            <TooltipProvider>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <div className="flex items-center">
                                                                            <InfoIcon className="h-4 w-4 text-amber-500" />
                                                                        </div>
                                                                    </TooltipTrigger>
                                                                    <KBTooltipContent tooltipText={`The model '${instruction.modelId}' is deprecated`} />
                                                                </Tooltip>
                                                            </TooltipProvider>
                                                        )}
                                                        {instruction.unique_name}
                                                    </div>
                                                    <Badge className="px-1 rounded text-green-50">
                                                        {instruction?.model_type}
                                                    </Badge>
                                                </div>
                                            </div>
                                        </Button>
                                    )))}
                            </div>
                        </ScrollArea>
                        {instructionDetails && (
                            <ScrollArea className="max-h-[400px] min-w-[480px] max-w-[480px] pr-4">
                                <Card className="w-full my-2">
                                    <CardHeader>
                                        <div className="flex items-start justify-between">
                                            <div className="space-y-1">
                                                <CardTitle className="text-xl whitespace-pre-wrap break-all">{instructionDetails.unique_name}</CardTitle>
                                                <CardDescription className="text-xs">Instruction</CardDescription>
                                            </div>

                                            <Button onClick={() => setInstructionUpdate(true)} size='icon'>
                                                <Pencil size={18} />
                                            </Button>

                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-2.5">
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2 text-xs">
                                                <User className="h-4 w-4" />
                                                <Label className="font-medium text-xs">Created By:</Label>
                                                <span className="text-muted-foreground">{instructionDetails.user_id || '-'}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-xs">
                                                <Clock className="h-4 w-4" />
                                                <Label className="font-medium text-xs">Modified By:</Label>
                                                <span className="text-muted-foreground">{instructionDetails.modified_by || '-'}</span>
                                            </div>
                                        </div>
                                        <hr />
                                        <div className="space-y-2">
                                            <div className="">
                                                <Label className="text-xs font-medium">Description</Label>
                                                <p className="text-xs text-muted-foreground">{instructionDetails.modelDescription || '-'}</p>
                                            </div>
                                            <div className="space-y">
                                                <Label className="text-xs font-medium">Prompt</Label>
                                                <p className="text-xs text-muted-foreground whitespace-pre-wrap break-all">{instructionDetails.prompt || '-'}</p>
                                            </div>
                                        </div>
                                        <hr />
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2">
                                                <Bot className="h-4 w-4" />
                                                <Label className="text-xs font-medium">Model ID</Label>
                                            </div>
                                            <code className="rounded bg-muted px-2 py-1 text-xs whitespace-pre-wrap break-all">{instructionDetails.modelId}</code>
                                        </div>
                                        <hr />
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2">
                                                <Settings2 className="h-4 w-4" />
                                                <Label className="text-xs font-medium">Parameters</Label>
                                            </div>
                                            <div className="grid gap-4 sm:grid-cols-2">
                                                {Object.entries(instructionDetails.parameters)
                                                    .filter(([key]) => key !== 'repetition_penalty' && key !== 'stop_sequence' && key !== 'n' && key !== 'stop' && key !== 'stream' && key !== 'seed' && key !== 'response_format')
                                                    .map(([key, value]) => (
                                                        <div className="" key={key}>
                                                            <Label className="text-xs font-medium">{handler.formatToTitleCase(key)}</Label>
                                                            <p className="text-sm text-muted-foreground">{value}</p>
                                                        </div>
                                                    ))
                                                }

                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                                <ScrollBar orientation="horizontal" />
                            </ScrollArea>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button type="button" variant="secondary">
                            Close
                        </Button>
                    </DialogClose>

                    {selectedInstruction &&
                        <Button variant="default" onClick={handleSelectInstruction} disabled={instruction?.unique_name === selectedInstruction}>
                            Select Instruction
                        </Button>
                    }
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
